import { Divider, FormControl, FormHelperText, InputLabel, ListSubheader, MenuItem, Select } from '@mui/material'
import { NextElementType } from '../data/NextElementDisplay'
import { LocalNext } from '../../domain/form/definition/LocalNext'
import { useFieldError } from '../../util/FormErrorBus'
import { useEffect, useMemo } from 'react'
import NextOption from './NextOption'

export type NextElementInputProps = {
  value?: LocalNext
  parentKey: string
  type: NextElementType
  onChange: (newValue: LocalNext | undefined) => void
  options?: readonly NextOption[]
}

const NextElementInput: React.FC<NextElementInputProps> = ({ value, parentKey, type, onChange, options = [] }) => {
  const defaultText = type === 'page' ? 'Next page' : 'Next item'
  const labelId = 'next-element-input-label'

  const { hasError, setError, clearError } = useFieldError(parentKey)
  useEffect(() => {
    const currentOption = value ? options?.find(({ next }) => next.key === value.key)?.label : undefined

    const fieldHasError = value && !currentOption
    if (fieldHasError) setError('')
    else clearError()
  }, [value, options])

  const pageOptions = useMemo(() => options.filter(({ next }) => next.type === 'page'), [options])
  const itemOptions = useMemo(() => options.filter(({ next }) => next.type === 'item'), [options])

  return (
    // Fixed width on the FormControl (the flex item): otherwise the Select grows with its selected
    // label and overflows narrow panels. Long labels truncate with an ellipsis; the dropdown still
    // shows them in full.
    <FormControl error={hasError} sx={{ width: 220, flexShrink: 0 }}>
      <InputLabel shrink id={labelId}>
        {'Go to'}
      </InputLabel>
      <Select
        labelId={labelId}
        label={'Go to'}
        // The Select is driven by the key string so it matches the MenuItem values; passing the
        // LocalNext object would make every value "out of range" for MUI.
        value={value?.key ?? ''}
        onChange={(e) => {
          const newValue = e.target.value
          if (newValue === '') {
            onChange(undefined)
          } else {
            const nextValue = options.find(({ next }) => next.key === newValue)?.next
            onChange(nextValue)
          }
        }}
        renderValue={(selectedKey) => {
          if (!selectedKey) return defaultText

          return options.find(({ next }) => next.key === selectedKey)?.label ?? selectedKey
        }}
        displayEmpty
      >
        <MenuItem value={''}>{defaultText}</MenuItem>
        {type === 'page' &&
          pageOptions.map(({ next, label }) => (
            <MenuItem key={next.key} value={next.key}>
              {label}
            </MenuItem>
          ))}

        {type === 'item' && itemOptions.length > 0 && (
          <ListSubheader disableSticky>Elements on this page</ListSubheader>
        )}
        {type === 'item' &&
          itemOptions.map(({ next, label }) => (
            <MenuItem key={next.key} value={next.key}>
              {label}
            </MenuItem>
          ))}
        {type === 'item' && pageOptions.length > 0 && <Divider component="li" sx={{ my: 0.5 }} />}
        {type === 'item' && pageOptions.length > 0 && <ListSubheader disableSticky>Other pages</ListSubheader>}
        {type === 'item' &&
          pageOptions.map(({ next, label }) => (
            <MenuItem key={next.key} value={next.key}>
              {label}
            </MenuItem>
          ))}
      </Select>
      {hasError && <FormHelperText error>{`Invalid ${value?.type} selected`}</FormHelperText>}
    </FormControl>
  )
}

export default NextElementInput
